import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { MediaObserver } from '@angular/flex-layout';
import { map, takeUntil } from 'rxjs/operators';

import { BUYER_SUPPORT_LINK, DISCOUNT_CODE_EXTERNAL_LINKS } from '~shared/constants/links.constants';
import { environment } from '~env/environment';
import { FooterNavigationLinks, NavigationLinks } from '../../../models/footer-navigation-links.model';
import { FooterLink } from '~app/modules/generic/models/footer-link.model';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { NavigationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventType, ApplicationNavigationEventType } from '~shared/modules/application-event/constants';
import { GaTrackingEventLabel, GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { Store } from '@ngrx/store';
import { UserInfoState } from '~shared/modules/user-info/store';
import {
  CALLBACK_CALENDLY_URLS,
  LOCAL_FOOTER_NAVIGATION_LINKS,
} from '~app/modules/generic/constants/footer-navigation-links.constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { CountryCode, FeatureToggleEnum } from '~shared/constants';
import {
  EXTERNAL_DOMAIN_TYPE,
  FOOTER_LABELS,
  FOOTER_NAVIGATION_LINKS,
  FOR_BUYERS_COLUMN,
  LINKS,
  PAYMENT_SVG_NAMES,
} from '~app/modules/generic/constants';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { htmlToPlainText } from '~app/modules/generic/helpers';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-footer-navigation',
  templateUrl: './footer-navigation.component.html',
  styleUrls: ['./footer-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterNavigationComponent implements OnInit, OnDestroy {
  @Input() isTier3: boolean;
  @Input() isSubsidiaryAccount: boolean;
  @Input() isMMCompanion: boolean = false;
  market: string;
  isMobile = true;
  destroySubject = new Subject();
  cdnBaseUrl = environment.cdnBaseUrl();
  links: FooterNavigationLinks;
  calendlyLink: string;
  currentUrl = this.navigationHelper.getCurrentUrl();
  isFFCoBrowserEnabled$: Observable<boolean>;
  isLoggedIn$: Observable<boolean>;
  isCoBrowserEnabled = false;
  regExp = /-/g;
  calendlyHref: string;

  get footerLinks(): Array<NavigationLinks> {
    if (this.isMMCompanion) {
      return this.links?.[FOOTER_NAVIGATION_LINKS].filter((link) => !link.hideOnMMCompanion);
    }

    return this.links?.[FOOTER_NAVIGATION_LINKS];
  }

  get tooltipHref(): string | null {
    const tooltipString = this.translateService.instant('FOOTER.ALL_PAGES.NAVIGATION.KUNDENSERVICE.TOOLTIP');
    if (!tooltipString) return null;
    const match = tooltipString.match(/href=['"]([^'"]+)['"]/);
    return match?.[1] || null;
  }

  public supportPhoneNoEnabled$: Observable<boolean> = this.featureFlagService.isFeatureEnabled(
    FeatureToggleEnum.FF_DISABLE_SUPPORT_PHONE_NO
  );

  constructor(
    private mediaObserver: MediaObserver,
    private applicationEventService: ApplicationEventService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private readonly store: Store<UserInfoState>,
    private cd: ChangeDetectorRef,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private identityService: IdentityService,
    private gtmDataLayerService: GtmDatalayerService,
    private navigationHelper: NavigationHelper,
    private translateService: TranslateService
  ) {
    this.market = getCountryDomainCode(this.webAppBuyerRequest);
  }

  ngOnInit() {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
      });
    this.calendlyLink = CALLBACK_CALENDLY_URLS[this.market];
    this.links = LOCAL_FOOTER_NAVIGATION_LINKS[this.market];
    this.featureFlagService
      .isFeatureEnabledAndReady(FeatureToggleEnum.FF_CHECKOUT_DISCOUNT_CODE)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((isEnabled) => {
        if (isEnabled) {
          this.links[FOOTER_NAVIGATION_LINKS][1][LINKS].unshift({
            domain: EXTERNAL_DOMAIN_TYPE,
            externalLink: DISCOUNT_CODE_EXTERNAL_LINKS[this.market],
            linkType: FOR_BUYERS_COLUMN,
            path: null,
            title: FOOTER_LABELS.DiscountCode,
            tooltip: null,
            tooltipPosition: null,
            trackingLabel: GaTrackingEventLabel.DiscountCode,
          });
          this.cd.detectChanges();
        }
      });

    this.initFFChecksForSofort();
    this.isLoggedIn$ = this.identityService.isLoggedIn();
    this.isFFCoBrowserEnabled$ =
      this.featureFlagService.isFeatureEnabledReadyForCurrentCountry$('FF_CCS_GENESYS_ACTIVATION');
    this.setCoBrowsing();
    const queryParams = {
      a2: this.currentUrl,
      utm_source: this.currentUrl,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    this.calendlyHref = `${this.calendlyLink}?${queryString}`;
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onLinkClicked(link: FooterLink): void {
    if (link.path !== BUYER_SUPPORT_LINK) {
      return;
    }

    this.applicationEventService.publish(
      new NavigationApplicationEvent(ApplicationNavigationEventType.HelpCenterClick)
    );
  }

  onCalendlyClick(): void {
    this.gtmDataLayerService.gtagEvent({
      category: ApplicationEventType.Interaction,
      action: 'Callback Calendly Click',
      label: 'Callback Calendly Click',
      event: 'Callback Calendly Click',
    });
  }

  onTelesalesPhoneClick(): void {
    this.gtmDataLayerService.gtagEvent({
      category: ApplicationEventType.Interaction,
      action: 'Callback Phone Click',
      label: 'Callback Phone Click',
      event: 'Callback Phone Click',
    });
  }

  onClickBuyerSupport() {
    this.applicationEventService.publish(
      new NavigationApplicationEvent(
        ApplicationNavigationEventType.FooterNavigationClick,
        GaTrackingEventLabel.BuyerSupport
      )
    );
  }

  private initFFChecksForSofort = (): void => {
    this.featureFlagService
      .isFeatureEnabled(FeatureToggleEnum.FF_MACO_FR_FOOTER_ENABLE_SOFORT)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((isEnabled: boolean) => {
        if (!isEnabled && this.market === CountryCode.France && this.links?.footer_for_payments.length > 0) {
          this.setPaymentLinksWithoutSofort();
        }
      });
  };

  private setPaymentLinksWithoutSofort = (): void => {
    this.links.footer_for_payments = this.links.footer_for_payments.filter(
      (footerPayment) => footerPayment !== PAYMENT_SVG_NAMES.sofortPayment
    );
  };

  private setCoBrowsing() {
    combineLatest([this.isLoggedIn$, this.isFFCoBrowserEnabled$])
      .pipe(
        takeUntil(this.destroySubject),
        map(([isLoggedIn, isFFEnabled]) => {
          if (isLoggedIn && isFFEnabled) {
            this.isCoBrowserEnabled = true;
            this.gtmDataLayerService.gtagEvent({
              event: GATrackingEventType.GenesysEnabled,
            });
          }
        })
      )
      .subscribe();
  }

  openCoBrowsing(): void {
    // @ts-ignore
    Genesys('subscribe', 'Messenger.ready', function () {
      // @ts-ignore
      Genesys(
        'command',
        'Messenger.open',
        {},
        () => {
          console.log('Messenger opened');
        },
        (error: any) => {
          console.log("Couldn't open messenger.", error);
        }
      );
    });
  }

  removeHTMLTags(text: string): string {
    return htmlToPlainText(text);
  }
}
